/**
 * @fileoverview Output Identity Hook
 *
 * Bu hook, hostOutput nesnesinin kimligini (identity) hesaplar.
 * Gereksiz useEffect tetiklemelerini onlemek icin kullanilir.
 *
 * @module hooks/useOutputIdentity
 */

import { useMemo } from "react";

/**
 * @typedef {Object} OutputIdentity
 * @property {string|null} kind - Yanit turu (listings, listing_detail, vb.)
 * @property {number} total - Toplam sonuc sayisi
 * @property {number} page - Mevcut sayfa numarasi
 * @property {string|null} path - Arama yolu
 * @property {string|null} filters - Filtre string'i
 * @property {string} listingsId - Ilan ID'lerinin birlestirimi
 * @property {string} projectsId - Proje ID'lerinin birlestirimi
 */

/**
 * Host output'tan kimlik bilgisi cikarir.
 *
 * Bu hook, hostOutput nesnesinin "anlami" degistiginde (icerik degistiginde)
 * yeni bir identity objesi dondurur. Referans esligi yerine icerik esligini
 * kontrol etmek icin kullanilir.
 *
 * **Kullanim Amaci:**
 * React'in useEffect dependency array'i referans esligi kullanir.
 * hostOutput objesi her render'da yeni referans alabilir.
 * Bu hook, gereksiz effect tetiklemelerini onler.
 *
 * @function useOutputIdentity
 * @param {Object|null} hostOutput - ChatGPT'den gelen tool output
 * @returns {OutputIdentity} Output kimlik bilgisi
 *
 * @example
 * const identity = useOutputIdentity(hostOutput);
 *
 * // identity degistiginde (icerik degistiginde) effect calisir
 * useEffect(() => {
 *   setOutput(hostOutput);
 * }, [identity.kind, identity.listingsId]);
 */
export function useOutputIdentity(hostOutput) {
  return useMemo(() => ({
    kind: hostOutput?.kind ?? null,
    total: hostOutput?.total ?? 0,
    page: hostOutput?.page ?? 1,
    path: hostOutput?.query?.path ?? null,
    filters: hostOutput?.query?.filters ?? null,
    listingsId: Array.isArray(hostOutput?.listings)
      ? hostOutput.listings.map((l) => l?.id ?? l?.url ?? "").join(",")
      : "",
    projectsId: Array.isArray(hostOutput?.projects)
      ? hostOutput.projects.map((p) => p?.id ?? p?.url ?? "").join(",")
      : "",
  }), [
    hostOutput?.kind,
    hostOutput?.total,
    hostOutput?.page,
    hostOutput?.query?.path,
    hostOutput?.query?.filters,
    hostOutput?.listings,
    hostOutput?.projects,
  ]);
}

export default useOutputIdentity;
